// NewDiscussionDialog.tsx
import React, { useState, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    CircularProgress,
    Alert
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { getUserDetails } from '../../../../../../../storageService';
import { addForumDiscussion } from '../../../../../../../Utils/apiService';



interface NewDiscussionDialogProps {
    open: boolean;
    onClose: () => void;
    forumId: string;
    onSuccess?: () => void;
}

const NewDiscussionDialog: React.FC<NewDiscussionDialogProps> = ({
    open,
    onClose,
    forumId,
    onSuccess
}) => {
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Get user details
    const profile = getUserDetails();
    const contactId = profile?.ContactId;

    const handleSave = async () => {
        if (!title.trim() || !message.trim()) {
            setError('Title and message are required');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const payload = {
                contactid: contactId,
                forumid: forumId,
                subject: title,
                message: message
            };

            await addForumDiscussion(payload);

            // Reset form
            setTitle('');
            setMessage('');

            // Close dialog and trigger success callback
            onClose();
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error('Error creating discussion:', err);
            setError('Failed to create discussion. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Quill editor modules/formats
    const modules = {
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link', 'image'],
            ['clean']
        ],
    };

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike', 'blockquote',
        'list', 'bullet',
        'link', 'image'
    ];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            sx={{ borderRadius: "12px" }}
        >
            <DialogTitle sx={{ fontSize: "16px", fontWeight: 500, padding: "16px 12px" }}>New Discussion</DialogTitle>
            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, padding: "16px" }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <label style={{ fontSize: "14px", fontWeight: 500 }}>Title</label>
                <TextField
                    variant="outlined"
                    placeholder="Enter title"
                    fullWidth
                    size="small"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                />

                <label style={{ fontSize: "14px", fontWeight: 500 }}>Message</label>
                <Box sx={{
                    height: '250px',
                    mb: 2,
                    '.ql-container': {
                        fontSize: '14px',
                        borderBottomLeftRadius: '4px',
                        borderBottomRightRadius: '4px',
                    },
                    '.ql-toolbar': {
                        borderTopLeftRadius: '4px',
                        borderTopRightRadius: '4px',
                    },
                    '.ql-editor': {
                        minHeight: '180px'
                    }
                }}>
                    <ReactQuill
                        theme="snow"
                        value={message}
                        onChange={setMessage}
                        modules={modules}
                        formats={formats}
                        placeholder="Enter your message"
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ padding: "16px", gap: 2 }}>
                <Button
                    onClick={onClose}
                    sx={{
                        backgroundColor: "#E6F1F8",
                        color: "#016AA7",
                        flex: 1,
                        "&:hover": { backgroundColor: "#d9e2ef" },
                    }}
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button
                    sx={{
                        backgroundColor: "#1E66D8",
                        color: "#FFFFFF",
                        flex: 1,
                        "&:hover": { backgroundColor: "#1554b3" },
                    }}
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading || !title.trim() || !message.trim()}
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Save and finish'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NewDiscussionDialog;